import { Modal, Form, Input } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import * as devicesApi from '../../api/devices.api'
import { useEffect } from 'react'
import { toast, handleMutationError } from '../../lib/notification'

interface Props {
  deviceId: string
  open: boolean
  onClose: () => void
}

export default function LockDeviceModal({ deviceId, open, onClose }: Props) {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const lockMutation = useMutation({
    mutationFn: (values: { reason: string; unlockPassword: string }) =>
      devicesApi.lockDevice(deviceId, values),
    onSuccess: (data) => {
      toast.success(data.message)
      form.resetFields()
      onClose()
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['devices', deviceId] })
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to lock device')
    },
  })

  useEffect(() => {
    if (!open) {
      form.resetFields()
    }
  }, [open, form])

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      lockMutation.mutate(values)
    })
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LockOutlined style={{ color: '#ef4444' }} />
          <span>Lock Device</span>
        </div>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Lock Device"
      okButtonProps={{
        danger: true,
        loading: lockMutation.isPending,
      }}
      width={600}
    >
      <div style={{ marginTop: 24 }}>
        <div
          style={{
            padding: 16,
            borderRadius: 12,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            marginBottom: 24,
          }}
        >
          <p style={{ margin: 0, color: '#991b1b', fontSize: 13, fontWeight: 500 }}>
            This will lock the device and display a full-screen message. The device will require
            a password or remote unlock to access.
          </p>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label="Lock Reason"
            rules={[{ required: true, message: 'Please enter a reason for locking' }, { max: 1000, message: 'Reason must not exceed 1000 characters' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="e.g., Payment overdue. Please contact support at +91-XXXXXXXXXX"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            name="unlockPassword"
            label="Unlock Password"
            rules={[
              { required: true, message: 'Please enter an unlock password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
            extra="The user can unlock locally using this password, or you can unlock remotely."
          >
            <Input.Password
              placeholder="Enter a secure password"
              style={{ borderRadius: 8, height: 40 }}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  )
}
